export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Parse various date formats to yyyy-mm-dd
 * Handles all Excel-supported date formats:
 * - Excel serial numbers (days since 1900-01-01)
 * - DD/MM/YYYY, DD-MM-YYYY, DD.MM.YYYY (European formats)
 * - MM/DD/YYYY, MM-DD-YYYY, MM.DD.YYYY (US formats)
 * - YYYY-MM-DD, YYYY/MM/DD, YYYY.MM.DD (ISO formats)
 * - DD/MM/YY, MM/DD/YY, YY/MM/DD (2-digit year formats)
 * - YYYYMMDD (compact format)
 * - "January 15, 1990", "15 Jan 1990", "Jan 15, 1990" (text formats)
 */
export const parseDateToYYYYMMDD = (dateValue: any): string => {
  if (!dateValue) return "";

  // If it's already in yyyy-mm-dd format, return as is
  if (typeof dateValue === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
    return dateValue;
  }

  // Handle Excel serial number (number of days since 1900-01-01)
  if (typeof dateValue === "number") {
    // Excel dates are number of days since 1900-01-01 (with a bug for 1900 leap year)
    const excelEpoch = new Date(1899, 11, 30); // Dec 30, 1899
    const date = new Date(
      excelEpoch.getTime() + dateValue * 24 * 60 * 60 * 1000
    );
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  const dateStr = String(dateValue).trim();

  // Helper to format date consistently
  const formatDate = (y: number, m: number, d: number): string => {
    const year = String(y).padStart(4, "0");
    const month = String(m).padStart(2, "0");
    const day = String(d).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Helper to expand 2-digit year (assumes 00-29 = 2000s, 30-99 = 1900s)
  const expandYear = (yy: number): number => {
    return yy < 30 ? 2000 + yy : 1900 + yy;
  };

  // Try parsing common date formats
  let parsedDate: Date | null = null;

  // Format: YYYYMMDD (compact, no separators)
  const compactYYYYMMDD = dateStr.match(/^(\d{4})(\d{2})(\d{2})$/);
  if (compactYYYYMMDD) {
    const [, year, month, day] = compactYYYYMMDD;
    return formatDate(parseInt(year), parseInt(month), parseInt(day));
  }

  // Format: YYYY/MM/DD, YYYY-MM-DD, or YYYY.MM.DD (ISO format - highest priority)
  const yyyymmdd = dateStr.match(/^(\d{4})[\/\-\.](\d{1,2})[\/\-\.](\d{1,2})$/);
  if (yyyymmdd) {
    const [, year, month, day] = yyyymmdd;
    parsedDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  }

  // Format: DD/MM/YYYY, DD-MM-YYYY, or DD.MM.YYYY (European format)
  if (!parsedDate) {
    const ddmmyyyy = dateStr.match(
      /^(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{4})$/
    );
    if (ddmmyyyy) {
      const [, day, month, year] = ddmmyyyy;
      const monthNum = parseInt(month);
      const dayNum = parseInt(day);
      // Only use European format if month is valid (1-12) and day > 12 (disambiguates from US format)
      // If both could be valid months, prefer European format
      if (monthNum >= 1 && monthNum <= 12 && dayNum <= 31) {
        parsedDate = new Date(parseInt(year), monthNum - 1, dayNum);
      }
    }
  }

  // Format: DD/MM/YY, DD-MM-YY, or DD.MM.YY (2-digit year European format)
  if (!parsedDate) {
    const ddmmyy = dateStr.match(/^(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{2})$/);
    if (ddmmyy) {
      const [, day, month, yy] = ddmmyy;
      const year = expandYear(parseInt(yy));
      parsedDate = new Date(year, parseInt(month) - 1, parseInt(day));
    }
  }

  // Format: YY/MM/DD, YY-MM-DD, or YY.MM.DD (2-digit year ISO-like format)
  if (!parsedDate) {
    const yymmdd = dateStr.match(/^(\d{2})[\/\-\.](\d{1,2})[\/\-\.](\d{1,2})$/);
    if (yymmdd) {
      const [, yy, month, day] = yymmdd;
      const yearNum = parseInt(yy);
      // If yy > 31, it's likely a year, not a day
      if (yearNum > 31) {
        const year = expandYear(yearNum);
        parsedDate = new Date(year, parseInt(month) - 1, parseInt(day));
      }
    }
  }

  // Format: MM/DD/YYYY (US format) - try if first number is valid month and second is valid day
  if (!parsedDate) {
    const mmddyyyy = dateStr.match(
      /^(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{4})$/
    );
    if (mmddyyyy) {
      const [, first, second, year] = mmddyyyy;
      const monthNum = parseInt(first);
      const dayNum = parseInt(second);
      // Use US format if first is valid month (1-12) and second is valid day (1-31)
      if (monthNum >= 1 && monthNum <= 12 && dayNum >= 1 && dayNum <= 31) {
        parsedDate = new Date(parseInt(year), monthNum - 1, dayNum);
      }
    }
  }

  // Try native Date parsing for text formats like:
  // "January 15, 1990", "15 Jan 1990", "Jan 15, 1990", "15-Jan-1990", etc.
  if (!parsedDate) {
    const nativeDate = new Date(dateStr);
    if (!isNaN(nativeDate.getTime())) {
      parsedDate = nativeDate;
    }
  }

  // If we successfully parsed the date, format it
  if (parsedDate && !isNaN(parsedDate.getTime())) {
    const year = parsedDate.getFullYear();
    const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
    const day = String(parsedDate.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  // Return original value if parsing failed
  return dateStr;
};
